import React from 'react';
import clsx from 'clsx';
import Icon from '../Icon';

type ButtonStyle = 'primary' | 'light' | 'danger';
type ButtonWidth = 'small' | 'medium' | 'big';

export type ButtonProps = {
  icon?: IconType;
  tooltip?: string;

  loading?: boolean;
  disabled?: boolean;
  type?: ButtonStyle;
  label?: React.ReactNode;
  children?: React.ReactNode;

  onClick?: () => void;

  width?: ButtonWidth;
  className?: string;
  textSizeClassName?: string;
};

const styleMap: { [x in ButtonStyle]: string } = {
  danger: 'bg-[#ef4444] text-bg border-[#ef4444]',
  light: 'bg-content hover:bg-active text-text border-active',
  primary: 'bg-active text-text border-active',
};

const widthMap: { [x in ButtonWidth]: string } = {
  small: 'px-lg',
  medium: 'px-2xl',
  big: 'px-3xl',
};

const Button: React.FC<ButtonProps> = ({
  icon,
  type = 'light',
  width = 'medium',

  tooltip,
  disabled: initialDisabled,
  label,
  children,
  loading,
  onClick,
  className,
  textSizeClassName = 'text-small',
}) => {
  const disabled = initialDisabled || loading;
  const content = children || label;

  return (
    <button
      title={tooltip}
      onClick={!disabled ? onClick : undefined}
      className={clsx(
        className,
        textSizeClassName,
        styleMap[type],
        content ? widthMap[width] : 'px-md',
        disabled ? 'disabled opacity-40 pointer-events-none' : '',
        'inline-flex items-center justify-center gap-x-sm',
        'py-sm font-medium shadow-md rounded-sm border',
        'transition-default'
      )}
    >
      {Boolean(icon || loading) && (
        <Icon icon={loading ? 'loading' : icon} width />
      )}
      {content}
    </button>
  );
};

export default React.memo(Button) as typeof Button;
